import React, {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';

import {
  comboKey,
  DEFAULT_HOTKEYS,
  Hotkey,
  HotkeySettings,
} from '../hotkeys';
import {
  exportHotkeySettings,
  loadHotkeySettings,
  parseImportedSettings,
  saveHotkeySettings,
  STORAGE_KEY_NAME,
} from '../services/storage';

interface HotkeySettingsContextValue {
  settings: HotkeySettings;
  setEnabled: (enabled: boolean) => void;
  setShowBadges: (showBadges: boolean) => void;
  addHotkey: (hotkey: Hotkey) => void;
  updateHotkey: (index: number, hotkey: Hotkey) => void;
  removeHotkey: (index: number) => void;
  resetDefaults: () => void;
  exportSettings: () => void;
  /** Throws with a readable message on invalid input. */
  importSettings: (json: string) => void;
  isComboTaken: (
    hotkey: Pick<Hotkey, 'code' | 'alt' | 'ctrl' | 'shift'>,
    ignoreIndex?: number
  ) => boolean;
}

const HotkeySettingsContext = createContext<HotkeySettingsContextValue | null>(
  null
);

export const HotkeySettingsProvider: FC<PropsWithChildren> = ({ children }) => {
  const [settings, setSettings] = useState<HotkeySettings>(() =>
    loadHotkeySettings()
  );

  const persist = (next: HotkeySettings) => {
    setSettings(next);
    saveHotkeySettings(next);
  };

  // Keep multiple tabs in sync.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY_NAME) setSettings(loadHotkeySettings());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setEnabled = (enabled: boolean) => {
    // Seed the defaults the first time the feature is switched on.
    const hotkeys =
      enabled && settings.hotkeys.length === 0
        ? DEFAULT_HOTKEYS
        : settings.hotkeys;
    persist({ ...settings, enabled, hotkeys });
  };

  const setShowBadges = (showBadges: boolean) =>
    persist({ ...settings, showBadges });

  const addHotkey = (hotkey: Hotkey) =>
    persist({ ...settings, hotkeys: [...settings.hotkeys, hotkey] });

  const updateHotkey = (index: number, hotkey: Hotkey) =>
    persist({
      ...settings,
      hotkeys: settings.hotkeys.map((existing, i) =>
        i === index ? hotkey : existing
      ),
    });

  const removeHotkey = (index: number) =>
    persist({
      ...settings,
      hotkeys: settings.hotkeys.filter((_, i) => i !== index),
    });

  const resetDefaults = () =>
    persist({ ...settings, hotkeys: DEFAULT_HOTKEYS });

  const exportSettings = () => exportHotkeySettings(settings);

  const importSettings = (json: string) => {
    const imported = parseImportedSettings(json);
    persist({
      ...settings,
      hotkeys: imported.hotkeys,
      showBadges: imported.showBadges ?? settings.showBadges,
    });
  };

  const isComboTaken = (
    hotkey: Pick<Hotkey, 'code' | 'alt' | 'ctrl' | 'shift'>,
    ignoreIndex?: number
  ) =>
    settings.hotkeys.some(
      (existing, index) =>
        index !== ignoreIndex && comboKey(existing) === comboKey(hotkey)
    );

  const value: HotkeySettingsContextValue = {
    settings,
    setEnabled,
    setShowBadges,
    addHotkey,
    updateHotkey,
    removeHotkey,
    resetDefaults,
    exportSettings,
    importSettings,
    isComboTaken,
  };

  return (
    <HotkeySettingsContext.Provider value={value}>
      {children}
    </HotkeySettingsContext.Provider>
  );
};

export const useHotkeySettings = (): HotkeySettingsContextValue => {
  const context = useContext(HotkeySettingsContext);
  if (!context) {
    throw new Error(
      'useHotkeySettings must be used within a HotkeySettingsProvider'
    );
  }
  return context;
};
